import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Bell, LogOut, Settings, ChevronDown, Shield, Building2, Briefcase, MessageSquare, MessageCircle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useMessageStore } from '../stores/messageStore';

export default function Header() {
  const { user, isAuthenticated, signOut } = useAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const dmUnread = useMessageStore((s) => s.unreadCount);
  const fetchDmUnread = useMessageStore((s) => s.fetchUnreadCount);
  const subscribeToUserInbox = useMessageStore((s) => s.subscribeToUserInbox);
  const unsubscribeFromUserInbox = useMessageStore((s) => s.unsubscribeFromUserInbox);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [dropdownNotifications, setDropdownNotifications] = useState<any[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 로그인 시 읽지 않은 알림 수 조회
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchUnreadCount();
      fetchDmUnread();
      // DM은 Realtime 구독 — 새 메시지 도착 시 즉시 unread 갱신
      subscribeToUserInbox(user.id);
      // 알림은 폴링 (Realtime 미적용), DM도 fallback으로 30초 폴링 유지
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchDmUnread();
      }, 30000);

      // 페이지 포커스 시 즉시 갱신 (탭 전환 시)
      const handleFocus = () => {
        fetchUnreadCount();
        fetchDmUnread();
      };
      window.addEventListener('focus', handleFocus);

      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        unsubscribeFromUserInbox();
      };
    }
  }, [isAuthenticated, user?.id, fetchUnreadCount, fetchDmUnread, subscribeToUserInbox, unsubscribeFromUserInbox]);

  // 외부 클릭 시 메뉴 닫기
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotificationMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    if (!confirm('로그아웃하시겠습니까?')) return;
    await signOut();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
      <nav className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="/logo/stampfit-logo.png"
            alt="StampFit"
            className="w-[132px] sm:w-[168px]"
          />
        </Link>

        {/* Auth */}
        <div className="flex items-center gap-4">
          {isAuthenticated && (
            <button
              onClick={() => navigate('/chat')}
              className="relative text-gray-500 hover:text-primary transition-colors"
              aria-label="DM 채팅"
              title="메시지"
            >
              <MessageCircle className="w-5 h-5" />
              {dmUnread > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {dmUnread > 99 ? '99+' : dmUnread}
                </span>
              )}
            </button>
          )}
          {isAuthenticated && (
            <div className="relative" ref={notificationRef}>
              <button
                onClick={async () => {
                  setShowNotificationMenu(!showNotificationMenu);
                  if (!showNotificationMenu) {
                    // 드롭다운용 로컬 상태로 읽지 않은 알림만 가져오기
                    try {
                      const response = await fetch('/api/v1/notifications?unread=true', {
                        credentials: 'include',
                      });
                      if (response.ok) {
                        const data = await response.json();
                        setDropdownNotifications(data.notifications || []);
                      }
                    } catch (error) {
                      console.error('Failed to fetch notifications:', error);
                    }
                  }
                }}
                className="relative text-gray-500 hover:text-primary transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotificationMenu && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">알림</h3>
                    {dropdownNotifications.length > 0 && (
                      <button
                        onClick={() => navigate('/messages')}
                        className="text-sm text-primary hover:text-primary-600"
                      >
                        모두 보기
                      </button>
                    )}
                  </div>
                  {dropdownNotifications.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">새로운 알림이 없습니다</p>
                    </div>
                  ) : (
                    <div className="py-1">
                      {dropdownNotifications.slice(0, 10).map((notification) => (
                        <button
                          key={notification.id}
                          onClick={() => {
                            // 메시지함으로 이동하면서 해당 메시지 ID를 파라미터로 전달
                            navigate(`/messages?id=${notification.id}`);
                            setShowNotificationMenu(false);
                          }}
                          className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-start gap-2">
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notification.created_at).toLocaleDateString('ko-KR', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {isAuthenticated && user ? (
            // 로그인 상태
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt="프로필"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-medium">
                    {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                  </div>
                )}
                <span className="text-gray-700 font-medium hidden sm:block">
                  {user.name || '사용자'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user.name || '사용자'}</p>
                    <p className="text-xs text-gray-500">{user.email || user.provider}</p>
                    {user.short_id && (
                      <p className="text-xs text-gray-400 mt-0.5">개인 고유번호 {user.short_id}</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4" />
                    내 정보
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/messages');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <MessageSquare className="w-4 h-4" />
                    알림
                    {unreadCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/chat');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <MessageCircle className="w-4 h-4" />
                    메시지
                    {dmUnread > 0 && (
                      <span className="ml-auto bg-accent text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                        {dmUnread > 99 ? '99+' : dmUnread}
                      </span>
                    )}
                  </button>
                  {(user.role === 'provider' || user.role === 'admin') && (
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/provider/posts');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Building2 className="w-4 h-4 text-accent" />
                      교육 기관
                    </button>
                  )}
                  {(user.role === 'employer' || user.role === 'admin') && (
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/employer/recruitment');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Briefcase className="w-4 h-4 text-emerald-600" />
                      기업 회원
                    </button>
                  )}
                  {user.role === 'admin' && (
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/admin');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-primary hover:bg-primary/5"
                    >
                      <Shield className="w-4 h-4" />
                      관리자
                    </button>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            // 비로그인 상태
            <Link
              to="/login"
              className="flex items-center gap-2 bg-primary hover:bg-primary-600 text-white font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <User className="w-4 h-4" />
              로그인
            </Link>
          )}
        </div>
      </nav>
    </header>
  );
}
